import { deleteDocument, uploadDocumentDirect } from "@/features/clipboard/api/clipboard";
import { useWorkspaceStorage } from "@/features/usage/hooks/useLimit";
import { reportLimitError } from "@/features/usage/limitErrors";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { consumePendingChatAttachments } from "../store/pendingAttachments";

const MAX_FILES = 3;
// Matches the backend limit (USER_MAX_FILE_SIZE), which in turn
// matches Datalab's 200MB per-file cap.
const MAX_SIZE_BYTES = 200 * 1024 * 1024; // 200MB

const SUPPORTED_TYPES = [
  {
    mime_type: "application/pdf",
    extensions: [".pdf"],
  },
  {
    mime_type: "text/markdown",
    extensions: [".md"],
  },
  {
    mime_type: "text/csv",
    extensions: [".csv"],
  },
];

const SUPPORTED_MIME_TYPES = SUPPORTED_TYPES.map((t) => t.mime_type);
export const SUPPORTED_EXTENSIONS = SUPPORTED_TYPES.flatMap((t) => t.extensions);

export interface ClipboardFile {
  filename: string;
  doc_id: string;
  short_id: string;
  size: number;
  /** True when this upload created the doc (vs deduplicated to an existing one). */
  newlyUploaded?: boolean;
  /** Explicit VFS path — set for items attached from the Clipboard page. */
  path?: string;
  /** "folder" for whole-folder attachments; defaults to a single file. */
  kind?: "file" | "folder";
}

export const useFileAttachments = () => {
  const filesRef = useRef<ClipboardFile[]>([]);
  const [, setVersion] = useState(0);
  const [pendingFileNames, setPendingFileNames] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { refetchUsage, isAtLimit: storageAtLimit } = useWorkspaceStorage();

  const setFiles = useCallback((next: ClipboardFile[]) => {
    filesRef.current = next;
    setVersion((v) => v + 1);
  }, []);

  // Consume any items handed off from the Clipboard page's "Start Chat" action.
  // These reference existing clipboard docs/folders (no upload), so they bypass
  // the upload limits and are never deleted on removal.
  useEffect(() => {
    const pending = consumePendingChatAttachments();
    if (pending.length === 0) return;
    const seeded: ClipboardFile[] = pending.map((p) => ({
      filename: p.name,
      doc_id: "",
      short_id: "",
      size: 0,
      newlyUploaded: false,
      path: p.path,
      kind: p.kind,
    }));
    setFiles([...filesRef.current, ...seeded]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      if (fileArray.length === 0) return;

      if (storageAtLimit) {
        toast.error("Workspace storage is full.");
        return;
      }

      const current = filesRef.current;
      if (current.length + fileArray.length > MAX_FILES) {
        toast.error(`You can only attach up to ${MAX_FILES} files`);
        return;
      }

      const validFiles: File[] = [];
      for (const file of fileArray) {
        if (current.some((f) => f.filename === file.name)) {
          continue;
        }
        if (!SUPPORTED_MIME_TYPES.includes(file.type)) {
          toast.error(`${file.name}: Unsupported file type.`);
          continue;
        }
        if (file.size > MAX_SIZE_BYTES) {
          toast.error(`${file.name}: File too large (max 200MB)`);
          continue;
        }
        validFiles.push(file);
      }

      if (validFiles.length === 0) {
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      setPendingFileNames(validFiles.map((f) => f.name));
      setIsProcessing(true);

      try {
        const results: ClipboardFile[] = [];
        for (const file of validFiles) {
          try {
            // Direct-to-GCS upload — file bytes bypass the backend entirely.
            const data = await uploadDocumentDirect(file, "chat_uploads");
            results.push({
              filename: file.name,
              doc_id: data.doc_id,
              short_id: data.short_id || `usr_${data.doc_id.replace(/-/g, "").slice(0, 12)}`,
              size: file.size,
              newlyUploaded: data.status === "success",
            });
          } catch (err) {
            console.error(`Failed to upload ${file.name}:`, err);
            const limitMessage = reportLimitError(err, "uploads", {
              refetchUsage,
              quotaAlertVisible: storageAtLimit,
            });
            if (!limitMessage) {
              toast.error(
                err instanceof Error ? err.message : `Failed to upload ${file.name}`,
              );
            }
          }
        }
        if (results.length > 0) refetchUsage();
        setFiles([...filesRef.current, ...results]);
      } finally {
        setPendingFileNames([]);
        setIsProcessing(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [setFiles, refetchUsage, storageAtLimit],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) {
        processFiles(e.target.files);
      }
    },
    [processFiles],
  );

  const removeFileByName = useCallback(
    (name: string) => {
      const removed = filesRef.current.find((f) => f.filename === name);
      setFiles(filesRef.current.filter((f) => f.filename !== name));
      // Already ingested on selection — clean up so removed chips don't
      // quietly consume quota. Never delete deduplicated pre-existing docs.
      if (removed?.newlyUploaded) {
        void deleteDocument(removed.doc_id).catch((err) => {
          console.warn(`Failed to clean up clipboard doc ${removed.doc_id}:`, err);
        });
      }
    },
    [setFiles],
  );

  const clearSelectedFiles = useCallback(() => {
    setFiles([]);
    setPendingFileNames([]);
  }, [setFiles]);

  const getSelectedFiles = useCallback(() => filesRef.current, []);

  return {
    fileInputRef,
    selectedFiles: filesRef.current,
    pendingFileNames,
    isProcessingFiles: isProcessing,
    getSelectedFiles,
    processFiles,
    handleFileInput,
    removeFileByName,
    clearSelectedFiles,
  };
};
